// src/components/home/EmergencyCTA.jsx
import React, { useEffect, useRef, useState } from "react";
import {
  Phone,
  MessageCircle,
  Clock3,
  ArrowUpRight,
  ShieldCheck,
} from "lucide-react";
import { siteConfig } from "../../homeComponents/data/siteConfig";

const EmergencyCTA = () => {
  const sectionRef = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const element = sectionRef.current;

    if (!element) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          observer.unobserve(element);
        }
      },
      {
        threshold: 0.15,
      }
    );

    observer.observe(element);

    return () => observer.disconnect();
  }, []);

  const phone = siteConfig.contact.phone;
  const whatsapp = siteConfig.contact.whatsapp.replace(/\D/g, "");

  return (
    <section
      ref={sectionRef}
      className="relative overflow-hidden bg-white py-12 sm:py-16 lg:py-20"
    >
      {/* Background decoration */}
      <div className="pointer-events-none absolute -left-32 top-10 h-72 w-72 rounded-full bg-[#F97316]/10 blur-3xl" />

      <div className="pointer-events-none absolute -right-32 bottom-0 h-80 w-80 rounded-full bg-[#00A260]/10 blur-3xl" />

      <div className="relative mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
        <div
          className={`relative overflow-hidden rounded-[2rem] bg-[#061A3A] shadow-2xl transition-all duration-700 ease-out ${
            visible
              ? "translate-y-0 opacity-100"
              : "translate-y-10 opacity-0"
          }`}
        >
          {/* Decorative circles */}
          <div className="pointer-events-none absolute -right-20 -top-20 h-64 w-64 rounded-full border border-white/10" />

          <div className="pointer-events-none absolute -right-10 -top-10 h-44 w-44 rounded-full border border-white/10" />

          <div className="pointer-events-none absolute -bottom-24 -left-24 h-56 w-56 rounded-full bg-[#F97316]/10 blur-2xl" />

          <div className="relative grid lg:grid-cols-[1fr_auto] lg:items-center">
            {/* Main content */}
            <div className="px-6 py-8 sm:px-8 sm:py-10 lg:px-12 lg:py-12">
              {/* Badge */}
              <div
                className={`inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/10 px-3 py-1.5 transition-all delay-100 duration-700 ${
                  visible
                    ? "translate-y-0 opacity-100"
                    : "translate-y-4 opacity-0"
                }`}
              >
                <span className="relative flex h-2.5 w-2.5">
                  <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-[#F97316] opacity-75" />
                  <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-[#F97316]" />
                </span>

                <span className="text-xs font-bold uppercase tracking-[0.16em] text-white/80">
                  24/7 Emergency Service
                </span>
              </div>

              {/* Heading */}
              <h3
                className={`mt-5 max-w-2xl text-3xl font-extrabold leading-tight text-white transition-all delay-200 duration-700 sm:text-4xl lg:text-5xl ${
                  visible
                    ? "translate-y-0 opacity-100"
                    : "translate-y-6 opacity-0"
                }`}
              >
                Need a vehicle{" "}
                <span className="text-[#F97316]">anytime?</span>
              </h3>

              {/* Description */}
              <p
                className={`mt-4 max-w-xl text-sm leading-6 text-white/70 transition-all delay-300 duration-700 sm:text-base sm:leading-7 ${
                  visible
                    ? "translate-y-0 opacity-100"
                    : "translate-y-6 opacity-0"
                }`}
              >
                Whether it is an urgent travel requirement, last-minute
                booking, or emergency vehicle rental, NJV Tours & Travels
                is ready to assist you.
              </p>

              {/* Service highlights */}
              <div
                className={`mt-6 flex flex-wrap gap-x-5 gap-y-3 transition-all delay-400 duration-700 ${
                  visible
                    ? "translate-y-0 opacity-100"
                    : "translate-y-5 opacity-0"
                }`}
              >
                <div className="flex items-center gap-2 text-xs text-white/70 sm:text-sm">
                  <Clock3 className="h-4 w-4 text-[#F97316]" />
                  Available Anytime
                </div>

                <div className="flex items-center gap-2 text-xs text-white/70 sm:text-sm">
                  <ShieldCheck className="h-4 w-4 text-[#00C878]" />
                  Reliable Service
                </div>
              </div>

              {/* Buttons */}
              <div
                className={`mt-8 flex flex-col gap-3 transition-all delay-500 duration-700 sm:flex-row ${
                  visible
                    ? "translate-y-0 opacity-100"
                    : "translate-y-5 opacity-0"
                }`}
              >
                {/* Call */}
                <a
                  href={`tel:${phone}`}
                  className="group inline-flex h-12 items-center justify-center gap-2 rounded-xl bg-[#F97316] px-6 text-sm font-bold text-white shadow-lg shadow-orange-500/20 transition-all duration-300 hover:-translate-y-0.5 hover:bg-[#EA580C] hover:shadow-xl"
                >
                  <Phone className="h-4 w-4" />

                  Call NJV Now

                  <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </a>

                {/* WhatsApp */}
                <a
                  href={`https://wa.me/${whatsapp}`}
                  target="_blank"
                  rel="noreferrer"
                  className="group inline-flex h-12 items-center justify-center gap-2 rounded-xl border border-white/20 bg-white/5 px-6 text-sm font-bold text-white transition-all duration-300 hover:-translate-y-0.5 hover:bg-white/10"
                >
                  <MessageCircle className="h-4 w-4 text-[#00C878]" />

                  WhatsApp NJV
                </a>
              </div>
            </div>

            {/* Right visual */}
            <div
              className={`hidden px-8 py-10 transition-all delay-500 duration-1000 lg:flex ${
                visible
                  ? "translate-x-0 opacity-100"
                  : "translate-x-10 opacity-0"
              }`}
            >
              <div className="relative flex h-52 w-52 items-center justify-center">
                {/* Outer ring */}
                <div className="absolute inset-0 animate-[spin_18s_linear_infinite] rounded-full border border-dashed border-white/15" />

                {/* Middle ring */}
                <div className="absolute inset-6 rounded-full border border-[#F97316]/30" />

                {/* Center */}
                <div className="relative flex h-32 w-32 flex-col items-center justify-center rounded-full bg-white/10 shadow-2xl backdrop-blur-sm">
                  <Phone className="h-9 w-9 text-[#F97316]" />

                  <span className="mt-2 text-xs font-bold uppercase tracking-wider text-white/80">
                    Call
                  </span>

                  <span className="mt-0.5 text-xs text-white/50">
                    Anytime
                  </span>
                </div>

                {/* Floating indicator */}
                <div className="absolute right-1 top-7 flex h-9 w-9 items-center justify-center rounded-full bg-[#00A260] shadow-lg shadow-green-500/20">
                  <span className="h-2.5 w-2.5 animate-pulse rounded-full bg-white" />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom note */}
        <p
          className={`mt-5 text-center text-xs text-[#64748B] transition-all delay-700 duration-700 sm:text-sm ${
            visible
              ? "translate-y-0 opacity-100"
              : "translate-y-4 opacity-0"
          }`}
        >
          Need a ride urgently?{" "}
          <span className="font-semibold text-[#061A3A]">
            Contact NJV Tours & Travels.
          </span>
        </p>
      </div>
    </section>
  );
};

export default EmergencyCTA;